import React, { forwardRef } from 'react';
import Tooltip from "@mui/material/Tooltip";
import { QuestionMarkCircleIcon, LinkIcon } from "@heroicons/react/24/outline";
import '../../styles/steps/BusinessBasics.css';
import { sampleFormData } from '../../utils/sampleData';

const industries = [
  { id: 'food_and_beverage', label: 'Food & Beverage', icon: '🍽️', enabled: true },
  { id: 'retail', label: 'Retail/E-commerce', icon: '🛍️', enabled: false },
  { id: 'services', label: 'Services', icon: '🔧', enabled: false },
  { id: 'fashion_and_apparel', label: 'Fashion & Apparel', icon: '👕', enabled: false },
  { id: 'beauty_and_cosmetics', label: 'Beauty & Cosmetics', icon: '💄', enabled: false },
  { id: 'tech_and_software', label: 'Tech & Software', icon: '💻', enabled: false },
  { id: 'health_and_wellness', label: 'Health & Wellness', icon: '🏥', enabled: false },
  { id: 'other', label: 'Other', icon: '📦', enabled: false },
];

const BusinessBasics = forwardRef(({ formData, setFormData, errors, setErrors, refs }, ref) => {
  // Set default industry to food_and_beverage when component mounts
  React.useEffect(() => {
    if (!formData.business.industry) {
      handleInputChange('industry', 'food_and_beverage');
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      business: {
        ...prev.business,
        [field]: value
      }
    }));

    // Clear error when user types
    if (errors?.business?.[field]) {
      setErrors(prev => ({
        ...prev,
        business: {
          ...prev.business,
          [field]: ''
        }
      }));
    }
  };

  return (
    <div className="step-container">
      {/* Sample Data Button - Always visible in development */}
      {/* {process.env.REACT_APP_ENV !== 'production' && (
        <button
          type="button"
          onClick={handleSampleDataSubmit}
          className="sample-data-button"
        >
          Load Sample Data
        </button>
      )} */}

      <h2 className="step-title">Let's get to know your business</h2>
      <p className="step-description">
        Start by telling us the essential details about your business
      </p>

      <div className="form-section">
        <div className="form-group">
          <div className="field-header">
            <label htmlFor="business-name">
              Business Name<span className="required-asterisk">*</span>
            </label>
            <Tooltip
              title="Your official business name"
              arrow
              placement="top"
            >
              <QuestionMarkCircleIcon className="help-icon" />
            </Tooltip>
          </div>
          <input
            ref={refs?.name}
            type="text"
            id="business-name"
            className={`form-input ${errors?.business?.name ? 'error' : ''}`}
            value={formData.business.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter your business name"
            required
            style={{ width: '100%' }}
          />
          {errors?.business?.name && (
            <div className="error-message">{errors.business.name}</div>
          )}
        </div>

        <div className="form-group">
          <div className="field-header">
            <label>
              Industry<span className="required-asterisk">*</span>
            </label>
            <Tooltip
              title="Currently supporting Food & Beverage industry only"
              arrow
              placement="top"
            >
              <QuestionMarkCircleIcon className="help-icon" />
            </Tooltip>
          </div>
          <div className="industry-grid">
            {industries.map((industry) => (
              <div key={industry.id} className="industry-button-container">
                <button
                  type="button"
                  className={`industry-button ${
                    formData.business.industry === industry.id ? 'selected' : ''
                  } ${!industry.enabled ? 'disabled' : ''}`}
                  onClick={() => industry.enabled && handleInputChange('industry', industry.id)}
                  disabled={!industry.enabled}
                >
                  <span className="industry-icon">{industry.icon}</span>
                  <span className="industry-label">{industry.label}</span>
                  {!industry.enabled && (
                    <span className="coming-soon-label">Coming Soon</span>
                  )}
                </button>
              </div>
            ))}
          </div>
          {errors?.business?.industry && (
            <div className="error-message">{errors.business.industry}</div>
          )}
        </div>

        <div className="form-group">
          <div className="field-header">
            <label htmlFor="business-description">
              Business Description<span className="required-asterisk">*</span>
            </label>
            <Tooltip
              title="Briefly describe what your business does"
              arrow
              placement="top"
            >
              <QuestionMarkCircleIcon className="help-icon" />
            </Tooltip>
          </div>
          <input
            ref={refs?.description}
            type="text"
            id="business-description"
            className={`form-input ${errors?.business?.description ? 'error' : ''}`}
            value={formData.business.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Brief description of your business, products and services"
            required
            style={{ width: '100%' }}
          />
          {errors?.business?.description && (
            <div className="error-message">{errors.business.description}</div>
          )}
        </div>

        <div className="form-group">
          <div className="field-header">
            <label htmlFor="business-location">
              Location/Service Area<span className="required-asterisk">*</span>
            </label>
            <Tooltip
              title="Where is your business located or where do you provide services?"
              arrow
              placement="top"
            >
              <QuestionMarkCircleIcon className="help-icon" />
            </Tooltip>
          </div>
          <input
            ref={refs?.location}
            type="text"
            id="business-location"
            className={`form-input ${errors?.business?.location ? 'error' : ''}`}
            value={formData.business.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            placeholder="Enter your business location"
            required
            style={{ width: '100%' }}
          />
          {errors?.business?.location && (
            <div className="error-message">{errors.business.location}</div>
          )}
        </div>

        <div className="form-group">
          <div className="field-header">
            <label htmlFor="business-address">
              Address
              <span className="optional-text">(Optional)</span>
            </label>
            <Tooltip
              title="Your business's physical address. This will be used to show in the Call To Action section of the content"
              arrow
              placement="top"
            >
              <QuestionMarkCircleIcon className="help-icon" />
            </Tooltip>
          </div>
          <input
            type="text"
            id="business-address"
            className={`form-input ${errors?.business?.address ? 'error' : ''}`}
            value={formData.business.address || ''}
            onChange={(e) => handleInputChange('address', e.target.value)}
            placeholder="Enter your business address"
            style={{ width: '100%' }}
          />
          {errors?.business?.address && (
            <div className="error-message">{errors.business.address}</div>
          )}
        </div>

        <div className="form-group">
          <div className="field-header">
            <label htmlFor="business-website">
              Website
              <span className="optional-text">(Optional)</span>
            </label>
            <Tooltip
              title="Your business website URL. This will be used to get additional data about your business"
              arrow
              placement="top"
            >
              <QuestionMarkCircleIcon className="help-icon" />
            </Tooltip>
          </div>
          <div className="input-with-icon">
            <LinkIcon className="input-icon" />
            <input
              type="url"
              id="business-website"
              className={`form-input ${errors?.business?.website ? 'error' : ''}`}
              value={formData.business.website}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  business: {
                    ...prev.business,
                    website: value,
                  },
                }));
                setErrors((prev) => ({
                  ...prev,
                  business: {
                    ...prev.business,
                    website: "",
                  },
                }));
              }}
              placeholder="https://www.yourbusiness.com"
              style={{ width: '100%' }}
            />
          </div>
          {errors?.business?.website && (
            <div className="error-message">{errors.business.website}</div>
          )}
        </div>


        <div className="form-group">
          <div className="field-header">
            <label htmlFor="business-phone">
              Phone Number
              <span className="optional-text">(Optional)</span>
            </label>
            <Tooltip
              title="Your business contact phone number. This will be used to show in the Call To Action section of the content"
              arrow
              placement="top"
            >
              <QuestionMarkCircleIcon className="help-icon" />
            </Tooltip>
          </div>
          <input
            type="tel"
            id="business-phone"
            className={`form-input ${errors?.business?.phone ? 'error' : ''}`}
            value={formData.business.phone || ''}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="(123) 456-7890"
            style={{ width: '100%' }}
          />
          {errors?.business?.phone && (
            <div className="error-message">{errors.business.phone}</div>
          )}
        </div>
{/* 
        <div className="form-group">
          <div className="field-header">
            <label htmlFor="year-established">
              Year Established<span className="required-asterisk">*</span>
            </label>
            <Tooltip
              title="When did you start your business?"
              arrow
              placement="top"
            >
              <QuestionMarkCircleIcon className="help-icon" />
            </Tooltip>
          </div>
          <input
            type="number"
            id="year-established"
            className={`form-input ${errors?.business?.year_established ? 'error' : ''}`}
            value={formData.business.year_established}
            onChange={(e) => handleInputChange('year_established', e.target.value)}
            placeholder="YYYY"
            min="1900"
            max={new Date().getFullYear()}
            required
          />
          {errors?.business?.year_established && (
            <div className="error-message">{errors.business.year_established}</div>
          )}
        </div> */}
      </div>
    </div>
  );
});

BusinessBasics.displayName = 'BusinessBasics';

export default BusinessBasics; 